import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import {
  DRIVER_APP_VERSION_NAME,
  DRIVER_APP_VERSION_CODE,
} from "../../appVersion.js";
import { registerPush } from "./client.js";

const WEB_ORIGIN = "https://backend.cargo-platform.online";
const PENDING_TOKEN_KEY = "driver.push.token.pending";
const SENT_TOKEN_KEY = "driver.push.token.lastSent";
const SENT_BINDING_KEY = "driver.push.token.lastSentBinding";

function readStorageValue(keys = []) {
  for (const key of keys) {
    try {
      const v1 = localStorage.getItem(key);
      if (v1) return String(v1).trim();
    } catch {}
    try {
      const v2 = sessionStorage.getItem(key);
      if (v2) return String(v2).trim();
    } catch {}
  }
  return "";
}

function buildAuthHeaders() {
  const headers = { "Content-Type": "application/json" };

  const token = readStorageValue([
    "driver_token",
    "drv_token",
    "driverToken",
    "token",
    "auth_token",
  ]);

  if (token) {
    headers.Authorization = /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
  }

  return headers;
}

function hasAuth() {
  return !!readStorageValue([
    "driver_token",
    "drv_token",
    "driverToken",
    "token",
    "auth_token",
  ]);
}

function currentAuthToken() {
  return readStorageValue([
    "driver_token",
    "drv_token",
    "driverToken",
    "token",
    "auth_token",
  ]);
}

async function currentAuthBindingFingerprint() {
  const token = currentAuthToken();
  if (!token) return "";
  try {
    if (!globalThis.crypto?.subtle || typeof TextEncoder !== "function") return "";
    const digest = await globalThis.crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(token)
    );
    return Array.from(new Uint8Array(digest).slice(0, 12))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return "";
  }
}

function dispatchMarketFreight(freightId) {
  const fid = String(freightId || "").trim();
  if (!fid) return false;

  try {
    localStorage.setItem("deep_market_freight_id", fid);
  } catch {}

  try {
    window.dispatchEvent(
      new CustomEvent("reisor_driver_open_market_freight", {
        detail: { freightId: fid },
      })
    );
  } catch {}

  return true;
}

function dispatchOrder(orderId) {
  const oid = String(orderId || "").trim();
  if (!oid) return false;

  try {
    localStorage.setItem("deep_order_id", oid);
  } catch {}

  try {
    window.dispatchEvent(
      new CustomEvent("reisor_driver_open_order", {
        detail: { orderId: oid },
      })
    );
  } catch {}

  return true;
}

function emitDriverFreightInviteEvent(name, data = {}) {
  try {
    const inviteId = String(data?.inviteId || data?.invite_id || "").trim();
    const freightId = String(data?.freightId || data?.freight_id || "").trim();
    window.dispatchEvent(
      new CustomEvent(name, {
        detail: { inviteId, freightId },
      })
    );
  } catch {}
}

function focusTargetFromPush(action) {
  try {
    const data = action?.notification?.data || action?.data || {};
    const type = String(data?.type || "").trim();
    const openTarget = String(data?.openTarget || data?.open_target || "").trim();

    if (
      type === "driver_available_vehicle_expiring" ||
      openTarget === "driver_my_vehicles"
    ) {
      const vehicleId = String(data?.vehicleId || data?.vehicle_id || "").trim();
      try {
        localStorage.setItem("reisor_driver_open_my_vehicles", "1");
        if (vehicleId) localStorage.setItem("reisor_driver_my_vehicle_focus_id", vehicleId);
      } catch {}

      try {
        window.dispatchEvent(
          new CustomEvent("reisor_driver_open_my_vehicles", { detail: { vehicleId } })
        );
      } catch {}

      return true;
    }

    if (
      type === "driver_own_freight_expired" ||
      openTarget === "driver_my_freights"
    ) {
      try {
        localStorage.setItem("reisor_driver_open_my_freights", "1");
      } catch {}

      try {
        window.dispatchEvent(new CustomEvent("reisor_driver_open_my_freights"));
      } catch {}

      return true;
    }

    const freightId = String(
      data?.freightId ||
      data?.freight_id ||
      ""
    ).trim();

    if (freightId && type === "driver_freight_invite") {
      emitDriverFreightInviteEvent("reisor_driver_freight_invite_action", data);
      return dispatchMarketFreight(freightId);
    }

    if (
      freightId &&
      (
        type === "market_expeditor_saved_freight_match" ||
        openTarget === "market_expeditor_freight"
      )
    ) {
      try {
        localStorage.setItem(
          "reisor_pro_entry_role",
          "EXPEDITOR"
        );

        localStorage.setItem(
          "reisor_pro_market_expeditor_deep_freight_id",
          freightId
        );
      } catch {}

      try {
        const u =
          new URL(
            window.location.href
          );

        u.searchParams.set(
          "marketExpeditorFreightId",
          freightId
        );

        window.location.assign(
          u.toString()
        );
      } catch {}

      return true;
    }

    if (
      freightId &&
      (
        type === "driver_saved_freight_match" ||
        openTarget === "driver_market_freight" ||
        String(data?.marketKey || "").startsWith("ext:")
      )
    ) {
      return dispatchMarketFreight(freightId);
    }

    const url = String(data?.url || "").trim();
    if (url) {
      try {
        const u = new URL(url, window.location.origin);
        const marketFreightId = String(
          u.searchParams.get("marketFreightId") ||
          u.searchParams.get("freightId") ||
          ""
        ).trim();
        if (marketFreightId) return dispatchMarketFreight(marketFreightId);
      } catch {}
    }

    const orderId = String(data?.orderId || data?.order_id || "").trim();
    if (orderId) return dispatchOrder(orderId);

    if (url) {
      try {
        const u = new URL(url, window.location.origin);
        const fromUrl = String(
          u.searchParams.get("orderId") ||
          u.searchParams.get("order_id") ||
          ""
        ).trim();
        if (fromUrl) return dispatchOrder(fromUrl);
      } catch {}
    }
  } catch {}

  return false;
}

async function sendTokenToBackend(safeToken) {
  const platform = String(Capacitor?.getPlatform?.() || "android").toLowerCase();

  const res = await fetch(`${WEB_ORIGIN}/api/push/register-token`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(),
    body: JSON.stringify({
      token: safeToken,
      platform,
      appVersion: DRIVER_APP_VERSION_NAME,
      appVersionCode: DRIVER_APP_VERSION_CODE,
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`DRIVER_PUSH_REGISTER_FAILED ${res.status} ${text.slice(0, 200)}`);
  }

  try {
    localStorage.setItem(SENT_TOKEN_KEY, safeToken);
    const binding = await currentAuthBindingFingerprint();
    if (binding) localStorage.setItem(SENT_BINDING_KEY, binding);
    else localStorage.removeItem(SENT_BINDING_KEY);
    localStorage.removeItem(PENDING_TOKEN_KEY);
  } catch {}

  try {
    window.dispatchEvent(new CustomEvent("reisor:push-channel-changed"));
  } catch {}
}

async function postToken(token) {
  const safeToken = String(token || "").trim();
  if (!safeToken) return;

  try {
    localStorage.setItem(PENDING_TOKEN_KEY, safeToken);
  } catch {}

  if (!hasAuth()) {
    console.log("[driver-push] token captured before auth, saved as pending");
    return;
  }

  try {
    const prev = localStorage.getItem(SENT_TOKEN_KEY);
    const prevBinding = localStorage.getItem(SENT_BINDING_KEY);
    const binding = await currentAuthBindingFingerprint();
    if (prev && prev === safeToken && binding && prevBinding === binding) return;
  } catch {}

  await sendTokenToBackend(safeToken);
}

async function flushPendingToken() {
  let token = "";
  try {
    token = String(localStorage.getItem(PENDING_TOKEN_KEY) || "").trim();
  } catch {}

  if (!token) return false;
  if (!hasAuth()) return false;

  try {
    await sendTokenToBackend(token);
    console.log("[driver-push] pending token flushed");
    return true;
  } catch (e) {
    console.warn("[driver-push] flushPendingToken failed:", e?.message || e);
    return false;
  }
}

export function useDriverPushBootstrap({ enabled = true, onAction } = {}) {
  const startedRef = useRef(false);
  const onActionRef = useRef(onAction);

  useEffect(() => {
    onActionRef.current = onAction;
  }, [onAction]);

  useEffect(() => {
    if (!enabled || startedRef.current) return;
    startedRef.current = true;

    registerPush({
      onToken: async (token) => {
        try {
          await postToken(token);
        } catch (e) {
          console.warn("[driver-push] postToken failed:", e?.message || e);
        }
      },
      onNotification: (notification) => {
        console.log("[driver-push] received:", notification);
        try {
          const data = notification?.data || {};
          if (String(data?.type || "").trim() === "driver_freight_invite") {
            emitDriverFreightInviteEvent("reisor_driver_freight_invite_push", data);
          }
        } catch {}
      },
      onAction: (action) => {
        try {
          focusTargetFromPush(action);
          onActionRef.current?.(action);
        } catch (e) {
          console.error("[driver-push] onAction ref failed:", e);
        }
      },
    }).catch((e) => {
      console.error("[driver-push] bootstrap failed:", e);
    });
  }, [enabled]);

  useEffect(() => {
    const tryFlush = () => {
      flushPendingToken().catch(() => {});
    };

    tryFlush();

    const onFocus = () => tryFlush();
    const onVisible = () => {
      if (document.visibilityState === "visible") tryFlush();
    };

    const timer = window.setInterval(tryFlush, 5000);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
}
